import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { I18nProvider } from "./contexts/I18nContext";
import { AuthProvider } from "./contexts/AuthContext";
import AuthCallback from "./components/AuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Matches from "./pages/Matches";
import MatchDetail from "./pages/MatchDetail";
import CreateMatch from "./pages/CreateMatch";
import Tournaments from "./pages/Tournaments";
import TournamentDetail from "./pages/TournamentDetail";
import Leagues from "./pages/Leagues";
import LeagueDetail from "./pages/LeagueDetail";
import Lessons from "./pages/Lessons";
import LessonDetail from "./pages/LessonDetail";
import Clubs from "./pages/Clubs";
import ClubDetail from "./pages/ClubDetail";
import CityLanding from "./pages/CityLanding";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import CalendarPage from "./pages/Calendar";
import Invoices from "./pages/Invoices";
import Notifications from "./pages/Notifications";
import CoachDashboard from "./pages/CoachDashboard";
import ClubDashboard from "./pages/ClubDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import Federation from "./pages/Federation";
import Rankings from "./pages/Rankings";
import PaymentSuccess from "./pages/PaymentSuccess";
import JoinByToken from "./pages/JoinByToken";
import ReferralLeaderboard from "./pages/ReferralLeaderboard";
import FacilityDetail from "./pages/FacilityDetail";
import FacilitiesDirectory from "./pages/FacilitiesDirectory";
import EnrollWithCoach from "./pages/EnrollWithCoach";
import PlayerPassport from "./pages/PlayerPassport";
import InstallBanner from "./components/InstallBanner";
import DevelopersPortal from "./pages/DevelopersPortal";

function AppRouter() {
  const location = useLocation();
  // Synchronous check — handle Emergent OAuth callback before normal routing
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />

      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/matches" element={<Matches />} />
      <Route path="/matches/create" element={<ProtectedRoute><CreateMatch /></ProtectedRoute>} />
      <Route path="/matches/:id" element={<MatchDetail />} />
      <Route path="/tournaments" element={<Tournaments />} />
      <Route path="/tournaments/:id" element={<TournamentDetail />} />
      <Route path="/leagues" element={<Leagues />} />
      <Route path="/leagues/:id" element={<LeagueDetail />} />
      <Route path="/lessons" element={<Lessons />} />
      <Route path="/lessons/:id" element={<LessonDetail />} />
      <Route path="/clubs" element={<Clubs />} />
      <Route path="/clubs/in/:city" element={<CityLanding />} />
      <Route path="/clubs/in/:city/:sport" element={<CityLanding />} />
      <Route path="/clubs/:id" element={<ClubDetail />} />

      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />

      <Route path="/coach" element={<ProtectedRoute roles={["coach", "admin"]}><CoachDashboard /></ProtectedRoute>} />
      <Route path="/club" element={<ProtectedRoute roles={["club_admin", "admin", "coach", "federation"]}><ClubDashboard /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/federation" element={<Federation />} />
      <Route path="/rankings" element={<Rankings />} />
      <Route path="/payments/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/join/:kind/:token" element={<JoinByToken />} />
      <Route path="/leaderboard" element={<ReferralLeaderboard />} />
      <Route path="/facilities" element={<FacilitiesDirectory />} />
      <Route path="/facilities/sport/:sport" element={<FacilitiesDirectory />} />
      <Route path="/facilities/in/:country" element={<FacilitiesDirectory />} />
      <Route path="/facilities/sport/:sport/in/:country" element={<FacilitiesDirectory />} />
      <Route path="/facilities/:idOrSlug" element={<FacilityDetail />} />
      <Route path="/enroll/coach/:token" element={<EnrollWithCoach />} />
      <Route path="/me/passport" element={<ProtectedRoute><PlayerPassport /></ProtectedRoute>} />
      <Route path="/u/:uid" element={<PlayerPassport publicView />} />
      <Route path="/developers" element={<DevelopersPortal />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <InstallBanner />
            <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#131313", color: "#fff", border: "1px solid #27272a" } }} />
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}
